import React from 'react';
import { PropTypes } from 'prop-types';
import { isEmpty, startCase } from 'lodash';
import pluralize from 'pluralize';
import { feeSchedulePrograms as fspUtils } from '@unite-us/client-utils';
import { CardDetail } from 'src/common/Card';
import formatShortDate from 'src/common/utils/Dates/formatShortDate';
import TimeDisplay from 'src/common/utils/Dates/TimeDisplay';
import { dollarAmount } from 'src/common/display/Money/DollarAmount';
import displaySavedClaimCodes from 'src/pages/invoices/utils/displaySavedClaimCodes';

const ServiceDetails = ({
  invoice,
  placeOfService,
  procedureCodes,
  procedureCodeModifiers,
  serviceDescription,
  showClaimsCodes,
  zCodes,
}) => {
  const isUnitBased = !fspUtils.isCostPaymentType(invoice.fee_schedule_program_payment_type);
  const unitAmountParens = dollarAmount(invoice.provided_service_unit_amount *
    invoice.fee_schedule_program_unit_rate, true);
  const unitAmount = isUnitBased ?
    `${invoice.provided_service_unit_amount} (${unitAmountParens})` :
    dollarAmount(invoice.provided_service_unit_amount, true);
  const unitRate = invoice.fee_schedule_program_unit_rate ?
    dollarAmount(invoice.fee_schedule_program_unit_rate, true) : '';

  const datesOfService = invoice.provided_service_ends_at ?
    `${formatShortDate(invoice.provided_service_starts_at)} - ${formatShortDate(invoice.provided_service_ends_at)}` :
    formatShortDate(invoice.provided_service_starts_at);
  const duration = invoice.billable_duration;

  return (
    <>
      {unitRate ? (
        <>
          <CardDetail label="Invoice Amount">
            {unitAmount}
          </CardDetail>
          <CardDetail label="Unit">
            {invoice.fee_schedule_program_unit}
          </CardDetail>
          <CardDetail label="Rate">
            {unitRate}
          </CardDetail>
        </>
      ) : (
        <CardDetail label="Amount Invoiced">
          {unitAmount}
        </CardDetail>
      )}
      <CardDetail label="Date(s) of Service">{datesOfService}</CardDetail>
      {invoice.fee_schedule_program_payment_type === 'time_based' && duration && (
        <>
          <CardDetail label="Start Time of Service">
            <TimeDisplay timestamp={invoice.provided_service_duration_start_time} />
          </CardDetail>
          <CardDetail label="Service Duration">
            {`${duration} ${pluralize('minute', duration)}`}
          </CardDetail>
        </>
      )}
      <CardDetail label="Service Provided">
        {invoice.fee_schedule_program_name}
      </CardDetail>
      {
        serviceDescription ? (
          <CardDetail label={'Service Description'}>{serviceDescription}</CardDetail>
        ) : null
      }
      <CardDetail label="Service Code">
        {invoice.fee_schedule_program_external_code}
      </CardDetail>
      <CardDetail label="Payment Approach">
        {startCase(invoice.fee_schedule_program_payment_type)}
      </CardDetail>
      <CardDetail label="Fee Schedule Name">{invoice.fee_schedule_name}</CardDetail>
      {
        showClaimsCodes && (
          <>
            {
              !isEmpty(zCodes) ? (
                <CardDetail label={'Zcodes'}>{displaySavedClaimCodes(zCodes)}
                </CardDetail>
              ) : null
            }
            {
              !isEmpty(placeOfService) ? (
                <CardDetail label={'Place of Service'}>{displaySavedClaimCodes(placeOfService)}
                </CardDetail>
              ) : null
            }
            {
              !isEmpty(procedureCodes) ? (
                <CardDetail label={'Procedure Codes'}>{displaySavedClaimCodes(procedureCodes)}</CardDetail>
              ) : null
            }
            {
              !isEmpty(procedureCodeModifiers) ? (
                <CardDetail label={'Procedure Code Modifier'}>
                  {displaySavedClaimCodes(procedureCodeModifiers)}
                </CardDetail>
              ) : null
            }
          </>
        )
      }
    </>
  );
};

ServiceDetails.propTypes = {
  invoice: PropTypes.object.isRequired,
  placeOfService: PropTypes.array,
  procedureCodes: PropTypes.array,
  procedureCodeModifiers: PropTypes.array,
  serviceDescription: PropTypes.string,
  showClaimsCodes: PropTypes.bool.isRequired,
  zCodes: PropTypes.array,
};

ServiceDetails.defaultProps = {
  placeOfService: [],
  procedureCodes: [],
  procedureCodeModifiers: [],
  serviceDescription: '',
  zCodes: [],
};

export default ServiceDetails;
